
import React from "react";
import { useNavigate } from "react-router-dom";

import { useDashboardStatsData } from "../hooks/useDashboardStatsData";
import { buildProgressItems } from "../utils/dashboardStats";
import { DASHBOARD_ROUTES } from "../homeDashboard/dashboardRoutes";

import HomeDashboardLayout from "../homeDashboard/HomeDashboardLayout";
import HeaderProfileSection from "../homeDashboard/HeaderProfileSection";
import MyWalletSidebar from "../homeDashboard/MyWalletSidebar";

import "../homeDashboard/DashboardSkeleton.css";
import "../dashboard/Dashboard.css";
import "../homeDashboard/HomeDashboard.css";
import "../homeDashboard/questionBank/QuestionPaperList.css";
import "./MyProgressPage.css";

import leafIcon from "../assets/images/leafIcon.png";

// Progress Card Component
const ProgressCard = ({ item }) => {
  const completed = Number(item.completed) || 0;
  const total = Number(item.total) || 0;

  const percentage =
    total > 0
      ? Math.min(100, Math.max(0, (completed / total) * 100))
      : 0;

  const colors = ["blue", "purple", "orange", "pink", "sky"];

  const colorClass =
    colors[(Number(item.toneIndex) || 0) % colors.length];

  return (
    <div className={`my-progress-cardd ${colorClass}`}>
      <div className="my-progress-icon-boxx">
        <span aria-hidden="true">
          {item.image ? (
            <img
              src={item.image}
              alt=""
              className="progress-category-image"
            />
          ) : (
            <i className="bi bi-bar-chart-fill" />
          )}
        </span>
      </div>

      <div className="my-progress-card-contentt">
        <div className="my-progress-card-topp">
          <div className="my-progress-card-headingg">
            <h3>{item.label}</h3>
            <p>Learning progress</p>
          </div>

          <span className="my-progress-countt">
            {completed}/{total}
          </span>
        </div>

        <div
          className="my-progress-trackk"
          role="progressbar"
          aria-label={`${item.label} progress`}
          aria-valuenow={Math.round(percentage)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="my-progress-filll"
            style={{ width: `${percentage}%` }}
          />
        </div>

        <span className="my-progress-percentagee">
          {Math.round(percentage)}% Completed
        </span>
      </div>
    </div>
  );
};

// Loading Skeleton
const ProgressCardSkeleton = () => (
  <div
    className="my-progress-cardd progress-card-skeleton"
    aria-hidden="true"
  >
    <span className="dashboard-skeleton dashboard-skeleton--line" />
  </div>
);

// Main My Progress Component
const MyProgress = () => {
  const navigate = useNavigate();

  const {
    data: stats = [],
    isLoading,
    isPlaceholderData,
    isFetching,
    isError,
    error,
    refetch,
  } = useDashboardStatsData();

  // Build progress cards dynamically from API data.
  const progressData = buildProgressItems(stats);

  const pending =
    isLoading || (isPlaceholderData && isFetching);

  const errorMessage =
    error?.message || "Unable to load your progress.";

  return (
    <HomeDashboardLayout showHeader={false}>
      <div className="qbl-page qbankList-page progress-page">
        <div className="qbl-main">
          <section className="qbl-panel">
            {/* Page Header */}
            <div className="hideProfile-page">
              <div className="categories-page-header mb-0">
                <div>
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={() => navigate(DASHBOARD_ROUTES.home)}
                    aria-label="Back to dashboard"
                  >
                    <i className="bi bi-chevron-left" />
                  </button>
                </div>

                <div />

                <div>
                  <HeaderProfileSection />
                </div>
              </div>
            </div>

            {/* Page Title */}
            <div className="qb-body">
              <div className="d-flex align-items-center justify-content-center">
                <div className="qb-copy">
                  <h1 className="qb-title">
                    My <span>Progress</span>
                  </h1>

                  <p className="qb-sub qbb-list">
                    Track your Learning Journey across all sections
                  </p>
                </div>

                <div className="leaf-icon">
                  <img src={leafIcon} alt="" />
                </div>
              </div>
            </div>

            {/* Progress Cards Section */}
            <div className="question-paper-listt">
              <div className="my-progress-pagee">
                {/* Error State */}
                {isError && (
                  <div
                    role="alert"
                    className="progress-status"
                  >
                    <p>{errorMessage}</p>

                    <button
                      type="button"
                      onClick={() => refetch()}
                      disabled={isFetching}
                    >
                      {isFetching ? "Retrying..." : "Try again"}
                    </button>
                  </div>
                )}

                {/* Loading State */}
                {pending && (
                  <p
                    role="status"
                    className="visually-hidden"
                  >
                    Loading your progress...
                  </p>
                )}

                {/* Empty State */}
                {!pending &&
                  !isError &&
                  progressData.length === 0 && (
                    <p role="status" className="progress-status">
                      No progress data available yet.
                    </p>
                  )}

                {/* Dynamic Progress Grid */}
                <div
                  className="my-progress-gridd"
                  aria-busy={pending}
                >
                  {pending &&
                    Array.from({ length: 6 }, (_, index) => (
                      <ProgressCardSkeleton key={index} />
                    ))}

                  {!pending &&
                    !isError &&
                    progressData.map((item) => (
                      <ProgressCard
                        key={item.id}
                        item={item}
                      />
                    ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <MyWalletSidebar withTopOffset={false} />
    </HomeDashboardLayout>
  );
};

export default MyProgress;
